import { Ray, Vector3 } from 'three';

import {
    createConnectorSnapPlane,
    findNearestConnectorSnapTarget,
    isTopBottomCameraView,
    raycastPlane,
    updateCursorRay
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';

import type { ConnectorSnapPoint, ConnectorSnapRange, PartPlacement, SettingModelSettingType } from 'api/models';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

interface ActivateManualSnapPlacementOptions {
    state: PlacementState;
    articleId: string;
    poiIndex: number;
    settingType: SettingModelSettingType;
    snapPoints: ConnectorSnapPoint[];
    snapRanges: ConnectorSnapRange[];
    referencePosition?: Vector3;
    useRay?: boolean;
}

export function getManualSnapCandidates(
    snapPoints: ConnectorSnapPoint[],
    snapRanges: ConnectorSnapRange[]
): PartPlacement[] {
    return [
        ...snapPoints.map((point) => point.placement),
        ...snapRanges.map((range) => ({ ...range.placement, position: range.startPoint }))
    ];
}

/**
 * Shared by solver-guided manual placement (connectors, anchors): anchors the drag plane at the snap target
 * nearest to the pointer and stores the solver targets. Returns false when no usable target exists.
 */
export function activateManualSnapPlacement({
    state,
    articleId,
    poiIndex,
    settingType,
    snapPoints,
    snapRanges,
    referencePosition,
    useRay = false
}: ActivateManualSnapPlacementOptions): boolean {
    const candidates = getManualSnapCandidates(snapPoints, snapRanges);
    if (candidates.length === 0) {
        return false;
    }

    updateCursorRay(state);
    const pointerRay = state.mainRaycasterRef.current?.ray;
    const firstPosition = candidates[0].position;
    const pointerSource =
        referencePosition ??
        state.pointerPositionRef.current ??
        new Vector3(firstPosition.x, firstPosition.y, firstPosition.z);
    const nearestTarget = findNearestConnectorSnapTarget(
        pointerSource,
        pointerRay ?? new Ray(),
        snapPoints,
        snapRanges,
        Number.POSITIVE_INFINITY,
        useRay && pointerRay !== undefined
    );
    const plane = createConnectorSnapPlane(nearestTarget?.placement ?? candidates[0]);
    if (!plane) {
        return false;
    }

    const selectedPoiGuid = state.pois[poiIndex]?.poi.poiGuid;
    const relatedPoiGuids = nearestTarget?.poiGuids.length
        ? nearestTarget.poiGuids
        : selectedPoiGuid
          ? [selectedPoiGuid]
          : [];
    const anchor = pointerRay?.intersectPlane(plane, new Vector3()) ?? plane.projectPoint(pointerSource, new Vector3());

    raycastPlane.setFromNormalAndCoplanarPoint(
        isTopBottomCameraView(raycastPlane.normal) ? raycastPlane.normal : plane.normal,
        anchor
    );
    state.pointerPositionRef.current = anchor.clone();
    state.setManualPlacementData(
        articleId,
        poiIndex,
        candidates,
        plane,
        settingType,
        snapPoints,
        snapRanges,
        relatedPoiGuids
    );
    return true;
}
